import { FomanticShim, PartialNullable } from "../../Extensions";
import { JNative } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean } from "../../RadObject";
import { AccordionPanel } from "./AccordionPanel";
import { ContainerControl, ContainerControlPropertyValues, IContainerControl } from "./ContainerControl";
import { IControl } from "./Control";
import { NAMESPACE } from "./_Namespace";

import "./Accordion.scss";

export type AccordionPropertyValues =
    ContainerControlPropertyValues
    & {
        allowMultiplePanelsOpen: boolean;
        setFirstPanelActive: boolean;
    };

export type AccordionProperties = {
    [name in keyof AccordionPropertyValues]: Property<AccordionPropertyValues[name]>
};

export interface IAccordion extends IContainerControl, AccordionProperties
{
}

export class Accordion extends ContainerControl implements IAccordion
{
    // #region Static

    public static override classNamespace = NAMESPACE + "Accordion";
    public static override className = "Accordion";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<AccordionPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                allowMultiplePanelsOpen: false,
                setFirstPanelActive: true,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Properties

    public allowMultiplePanelsOpen!: PropertyBoolean;
    public setFirstPanelActive!: PropertyBoolean;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<AccordionPropertyValues>)
    {
        super(propertyValues);

        const
            self = this;

        self.addCssClass("rad-control-accordion ui accordion fluid styled");

        if (self.setFirstPanelActive.get())
            self._setFirstPanelActive();

        FomanticShim(self.$$element.get())
            .accordion({
                exclusive: !self.allowMultiplePanelsOpen.get(),
                onClose: function ()
                {
                    self._triggerAncestorVisibilityChanged(new PropertyChange<boolean>(false, true));
                },
                onClosing: function ()
                {
                    this.closest(".rad-control-accordion-panel").removeClass("rad-state-open");
                },
                onOpen: function ()
                {
                    self._triggerAncestorVisibilityChanged(new PropertyChange<boolean>(true, false));
                },
                onOpening: function ()
                {
                    self.controlsGet().forEach(item => { item.removeCssClass("rad-state-open"); });
                    this.closest(".rad-control-accordion-panel").addClass("rad-state-open");
                },
                selector: {
                    trigger: '>.rad-control-accordion-panel>.title'
                }
            });
    }

    // #endregion

    // #region Public Methods

    public allClosed()
    {
        return !this.controlsGet().some(item => item.isOpen());
    }
    public close(index: number, immediately: boolean)
    {
        if (index < 0)
            return;

        if (immediately)
        {
            const
                accordionPanels = this.controlsGet();

            if (!accordionPanels || index > accordionPanels.length - 1)
                return;

            accordionPanels[index].removeActiveClass();
        }
        else
            FomanticShim(this.$$element.get()).accordion("close", index);
    }
    public closeAll(immediately: boolean)
    {
        this.controlsGet().forEach(item => { item.close(immediately); });
    }
    public open(index: number)
    {
        FomanticShim(this.$$element.get()).accordion("open", index);
    }

    // #endregion

    // #region Protected Methods

    protected _allowMultiplePanelsOpenChanged(change: PropertyChange<boolean>)
    {
        const
            $$element = this.$$element.get();

        if (!$$element)
            return;

        FomanticShim($$element).accordion("setting", { exclusive: !change.currentValue });
    }
    protected _setFirstPanelActive()
    {
        const
            controls = this.controlsGet();

        if (controls.length === 0)
            return;

        controls[0]._titleElementRef.current?.addClass("active");
        controls[0]._containerWrapperElementRef.current?.addClass("active");
        controls[0].show();
        controls[0]._updateLayout()
    }
    protected _setFirstPanelActiveChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: AccordionPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.allowMultiplePanelsOpen = new PropertyBoolean(this, propertyValues.allowMultiplePanelsOpen, this._allowMultiplePanelsOpenChanged);
        this.setFirstPanelActive = new PropertyBoolean(this, propertyValues.setFirstPanelActive, this._setFirstPanelActiveChanged);
    }

    public override append($$elementOrControlOrArrayOf: JNative | IControl | IControl[], beforeSelector?: any)
    {
        super.append($$elementOrControlOrArrayOf, beforeSelector);

        if (this.setFirstPanelActive.get() && this.controlsGet().length === 1)
            this._setFirstPanelActive();

        return this;
    }
    public override controlsGet(recursive?: boolean)
    {
        return super.controlsGet(recursive) as AccordionPanel[];
    }

    // #endregion

    // #endregion
}